import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useT, LangToggle } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../contexts/AuthProvider';
import logo from '../assets/calum_logo_white.png';

// 16px on phones so iOS doesn't zoom into the field; compact on desktop.
const FIELD = 'w-full rounded-lg border border-slate-200 px-3 py-2.5 sm:py-2 text-[16px] sm:text-sm outline-none focus:border-[#cf9a40]';

const Login = () => {
  const t = useT();
  const { isAuthenticated, requestCode, verifyCode } = useAuth();
  const [step, setStep] = useState('email');      // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState('');
  const [devCode, setDevCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { document.title = 'Login | Perfy · Calum'; }, []);
  if (isAuthenticated) return <Navigate to="/overview" replace />;

  const onRequest = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const data = await requestCode(email.trim());
      setChallenge(data.challenge);
      setDevCode(data.dev_code || '');
      setStep('code');
    } catch (err) {
      setError(err?.response?.data?.detail || t('Versturen mislukt. Probeer opnieuw.'));
    } finally { setBusy(false); }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await verifyCode(email.trim(), code.trim(), challenge);
      // isAuthenticated flips -> redirect happens
    } catch (err) {
      setError(err?.response?.data?.detail || t('Verkeerde code.'));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <Card className="relative w-full max-w-md shadow-lg border border-slate-200 bg-white">
        <LangToggle className="absolute right-3 top-3" />
        <CardHeader className="space-y-3 text-center">
          <div className="flex justify-center">
            <img src={logo} alt="Calum" className="h-20 w-auto" />
          </div>
          <CardTitle style={{ fontFamily: 'Outfit, Inter, sans-serif' }}>Perfy · Calum</CardTitle>
          <p className="text-sm text-slate-500">{t("café analytics")}</p>
        </CardHeader>
        <CardContent className="space-y-5">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}

          {step === 'email' ? (
            <form onSubmit={onRequest} className="space-y-3">
              <label htmlFor="login-email" className="block text-sm font-medium text-slate-600">{t("E-mailadres")}</label>
              <input id="login-email" type="email" inputMode="email" autoComplete="email" required autoFocus
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="naam@voorbeeld.be" className={FIELD} />
              <Button type="submit" disabled={busy} className="w-full bg-[#114817] hover:bg-[#0d3712] text-white">
                {busy ? t('Versturen…') : t('Stuur inlogcode')}
              </Button>
            </form>
          ) : (
            <form onSubmit={onVerify} className="space-y-3">
              <p className="text-sm text-slate-600">{t("We stuurden een 6-cijferige code naar")} <b>{email}</b>.</p>
              {devCode && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {t("Dev-modus — je code is")} <b className="tabular-nums">{devCode}</b>
                </div>
              )}
              <input id="login-code" type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code"
                maxLength={6} required autoFocus value={code} aria-label={t('Inlogcode')}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="••••••"
                className="w-full rounded-lg border border-slate-200 px-3 py-2.5 sm:py-2 text-center text-lg tracking-[0.5em] tabular-nums outline-none focus:border-[#cf9a40]" />
              <Button type="submit" disabled={busy || code.length !== 6} className="w-full bg-[#114817] hover:bg-[#0d3712] text-white">
                {busy ? t('Controleren…') : t('Inloggen')}
              </Button>
              <button type="button" onClick={() => { setStep('email'); setCode(''); setError(''); }}
                className="w-full text-xs text-slate-400 hover:text-slate-600 py-2">← {t("ander e-mailadres")}</button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
